import "/node_modules/flag-icons/css/flag-icons.min.css"
import { ChangeEvent, Suspense, useId, useMemo, useRef, useState } from "react"
import { IMaskInput } from "react-imask"
import { CaretDown } from "@phosphor-icons/react"
import { debounce } from "es-toolkit/function"
import { t } from "i18next"
import { z } from "zod"
import { Banner } from "@/components/ui/banner.tsx"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx"
import { FormError } from "@/components/ui/form.tsx"
import { Input } from "@/components/ui/input.tsx"
import { ScrollArea } from "@/components/ui/scroll-area"
import { checkIfSMSEnabled, DefaultMask } from "@/lib/sms-enabled-phone-number.ts"
import { cn } from "@/lib/utils.ts"
import { ExistingPhoneNumberList } from "@/modules/patients/components/phoneNumber/ExistingPhoneNumberList.tsx"
import { useExistingPhoneNumberList } from "@/modules/patients/components/phoneNumber/hooks/useExistingPhoneNumberList.tsx"
import { usePhoneNumberCountryList } from "@/modules/patients/components/phoneNumber/hooks/usePhoneNumberCountryList.tsx"

type CountryOption = {
  id: string
  label: string
  value: string
  code: string
  mask: string
  dialCode: string
}

interface PhoneNumberInputProps {
  value: string | undefined
  label: string
  onChange: (value: string | undefined) => void
  error: string
  isSMSSupported?: boolean
  isCorrespondence?: boolean
  isCareProvider?: boolean
  classes?: { buttonClass?: string; inputClass?: string }
  ref?: React.Ref<HTMLInputElement | null>
  originalValue?: string
  disabled?: boolean
}

const checkIsSmsNumberValid = (country?: CountryOption, phoneNumber?: string) => {
  if (!country || !phoneNumber) return false
  return checkIfSMSEnabled(country.code, phoneNumber)
}

const PhoneNumberInputField = ({
  value,
  label,
  error,
  onChange,
  isSMSSupported = false,
  isCorrespondence = false,
  isCareProvider = false,
  classes,
  ref,
  originalValue,
  disabled,
}: PhoneNumberInputProps) => {
  const id = useId()
  const [keyword, setKeyword] = useState("")
  const { getCountryByPhoneNumber, filteredCountryOptions, mostRecent, countryOptions } =
    usePhoneNumberCountryList(keyword)
  const defaultSwitzerlandCountry = useMemo(
    () => countryOptions.find((country) => country.code === "CH"),
    [countryOptions],
  )
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | undefined>(
    value ? getCountryByPhoneNumber(value) : defaultSwitzerlandCountry,
  )
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(value || defaultSwitzerlandCountry?.dialCode)

  const initialCountry = value ? getCountryByPhoneNumber(value) : defaultSwitzerlandCountry
  const initialPhoneNumber = value || defaultSwitzerlandCountry?.dialCode

  const [isSMSNumberValid, setIsSMSNumberValid] = useState<boolean>(
    checkIsSmsNumberValid(initialCountry, initialPhoneNumber),
  )
  const { existingPhoneNumberLists } = useExistingPhoneNumberList(isCorrespondence)
  const internalRef = useRef(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const debouncedParentOnChange = useMemo(() => debounce((value: string) => onChange?.(value), 350), [onChange])

  const handleSelectCountry = (country: CountryOption) => {
    setSelectedCountry(country)
    setPhoneNumber(country.dialCode)
    onChange(country.dialCode)
  }

  const onChangePhoneNumber = (value: string) => {
    const prefixValue = value ? (value.startsWith("+") ? value : `+${value}`) : value
    setPhoneNumber(prefixValue)
    if (prefixValue.length < 6) {
      const country = getCountryByPhoneNumber(prefixValue)
      setSelectedCountry(country)
    }
    const isSMSEnabled = checkIfSMSEnabled(selectedCountry?.code || "", prefixValue)
    setIsSMSNumberValid(isSMSEnabled)
    debouncedParentOnChange(prefixValue)
  }

  const onChangeExistingPhoneNumber = (existingPhoneNumber: string) => {
    const country = getCountryByPhoneNumber(existingPhoneNumber)
    setPhoneNumber(existingPhoneNumber)
    setSelectedCountry(country)
    const isSMSEnabled = checkIfSMSEnabled(country?.code ?? "", existingPhoneNumber ?? "")
    setIsSMSNumberValid(isSMSEnabled)
    onChange?.(existingPhoneNumber)
  }

  const DropdownMenuItem = ({ options }: { options: CountryOption[] }) => {
    return options.map((item: CountryOption) => (
      <DropdownMenuCheckboxItem
        className="h-9"
        key={item.id}
        checked={item?.dialCode === selectedCountry?.dialCode}
        onCheckedChange={() => handleSelectCountry(item)}>
        <span className={`fi fis fi-${item.code.toLowerCase()} mr-1 h-5.5! w-5.5! rounded-xs`}></span>
        {item.value} ({item.dialCode})
      </DropdownMenuCheckboxItem>
    ))
  }

  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    debouncedParentOnChange.cancel()
    let pastedValue = event.clipboardData.getData("text")

    const input = event.target
    if (input instanceof HTMLInputElement) {
      const selectionStart = input.selectionStart ?? 0
      const selectionEnd = input.selectionEnd ?? 0
      const currentValue = input.value
      pastedValue = currentValue.slice(0, selectionStart) + pastedValue + currentValue.slice(selectionEnd)
    }

    // Remove all non-digit characters and (0)
    // Replace leading 0 with 41 (Switzerland as default)
    // Remove leading 00 for French numbers
    pastedValue = pastedValue.replace(/(^00|(?:\(0\))|\D)/g, "").replace(/^0+/, "41")

    const country = getCountryByPhoneNumber(`+${pastedValue}`)
    if (country) {
      setSelectedCountry(country)
      if (country.mask) {
        const maskDigits = country.mask.replace(/\D/g, "").length
        // If pasted value has more digits than mask, trim it
        pastedValue = pastedValue.slice(0, maskDigits)
      }
    }
    onChangePhoneNumber(pastedValue)
  }

  const onBlur = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0 && originalValue) {
      const country = getCountryByPhoneNumber(originalValue)
      if (country) {
        setSelectedCountry(country)
      }
      onChangePhoneNumber(originalValue)
    }
  }

  return (
    <div className="w-full flex-col" data-testid="phone-number-section">
      <div
        className={cn(
          "focus-within:outline-primary flex h-12 w-full items-center rounded-md",
          error
            ? "outline-critical! focus-within::outline-2! overflow-hidden outline-1 outline-solid focus-within:outline-2"
            : "focus-within:outline-0",
        )}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={disabled}>
            <button
              id="dropdown-phone-button"
              data-dropdown-toggle="dropdown-phone"
              className={cn(
                "border-border bg-surface-subdued text-on-surface hover:bg-surface-subdued-hovered z-10 inline-flex h-12.75 items-center rounded-s-lg border pt-3 pr-2.5 pb-3 pl-2.5 text-center text-sm font-medium focus:outline-hidden",
                classes?.buttonClass,
              )}
              type="button">
              <div className="grid w-full grid-flow-col items-center rounded-xl select-none">
                <span
                  className={`fi fis fi-${selectedCountry?.code?.toLowerCase()} mr-1 h-5.5! w-5.5! rounded-xs`}></span>
                <CaretDown size={24}></CaretDown>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="bottom"
            sideOffset={4}
            align="start"
            data-testid="language-filter-content"
            className="z-1000 grid w-94 rounded-md border-none pt-2 leading-5">
            <div className="px-2">
              <Input label="Search" onChange={(e) => setKeyword(e.target.value)} value={keyword} />
              <DropdownMenuSeparator className="bg-border -mx-2 mt-2 mb-0" />
            </div>
            <ScrollArea viewportClassName="max-h-100">
              <div className="p-2">
                {!keyword ? (
                  <DropdownMenuItem options={mostRecent} />
                ) : (
                  <div className="grid gap-0.5">
                    <DropdownMenuItem options={filteredCountryOptions} />
                  </div>
                )}
              </div>
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="relative w-full">
          <IMaskInput
            id={id}
            data-testid="phone-number-input"
            className={cn(
              "peer border-border bg-surface-subdued text-on-surface placeholder:text-on-surface-subdued hover:bg-surface-subdued-hovered z-1 h-12.75 w-full rounded-e-lg border p-3 px-1.5 pt-[1.7875rem] text-sm outline-offset-0 transition-all focus:outline-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
              classes?.inputClass,
              error && "outline-critical!",
            )}
            value={phoneNumber}
            mask={selectedCountry?.mask || DefaultMask}
            unmask={true}
            ref={internalRef}
            inputRef={(node) => {
              inputRef.current = node
              // we need to check type here because ref from react-hook-form is a function
              if (typeof ref === "function") {
                ref(node)
              } else if (ref?.current) {
                ref.current = node
              }
            }}
            disabled={disabled}
            onAccept={(value, _, event) => {
              // Only process user input, not programmatic value changes (e.g., country selection)
              if (event) {
                onChangePhoneNumber(value)
              }
            }}
            onPaste={onPaste}
            onBlur={onBlur}
          />
          <label
            htmlFor={id}
            className={cn(
              "text-on-surface-subdued peer-focus:text-primary absolute top-1 left-4 text-xs transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs",
              error && "text-critical peer-placeholder-shown:text-on-surface-subdued peer-focus:text-critical",
            )}>
            {label}
          </label>
          {!isCareProvider && existingPhoneNumberLists?.length > 0 && !disabled && (
            <div className="absolute top-1 right-4 z-10">
              <ExistingPhoneNumberList
                onSelect={onChangeExistingPhoneNumber}
                phoneNumberList={existingPhoneNumberLists}
              />
            </div>
          )}
        </div>
      </div>
      {error && <FormError>{error}</FormError>}
      {isSMSSupported && !isSMSNumberValid && (
        <div className="mt-2">
          <Banner variant="warning" type="inline">
            <span className="text-inherit">{t("common.fieldErrorList.notSMSSupported")}</span>
          </Banner>
        </div>
      )}
    </div>
  )
}

const PhoneNumberInput = (props: PhoneNumberInputProps) => (
  <Suspense fallback={null}>
    <PhoneNumberInputField {...props} />
  </Suspense>
)

const phoneNumberValidation = (validateFn: (val: string) => boolean | Promise<boolean>) =>
  z.string({ error: "common.fieldErrorList.requiredField" }).refine(async (value) => {
    if (!value) return z.NEVER

    return await validateFn(value)
  }, "common.fieldErrorList.invalidFormat")

export { PhoneNumberInput, phoneNumberValidation }
